import { MouseEventHandler, useRef, useState } from 'react';
import {
  Box,
  Button,
  forwardRef,
  Icon,
  LinkOverlay,
  Tooltip,
  TypeColors,
  TypeIconName,
  useResponsive,
} from '@bitrise/bitkit';
import { Build, getBuildStatusColors, getBuildStatusIconName, getBuildStatusText } from '@/models/Build';
import { AppAvatar, AppTitle } from '@/pages/dashboard/AppsSection/AppListItem';
import BuildMetaData from '@/components/BuildListItem/BuildMetaData/BuildMetaData';
import { buildDetailsPagePath, pipelineDetailsPagePath } from '@/utils/monolithApiRouteService';
import { getGitProviderCommitHashUrl, getGitProviderPullRequestUrl } from '@/models/GitProvider';
import AppForAppPage from '@/pages/App/types/AppForAppPage';
import InstallableArtifacts from './InstallableArtifacts/InstallableArtifacts';
import PullRequestOrCommitHashLink from './PullRequestOrCommitHashLink/PullRequestOrCommitHashLink';

type BuildStatusIconProps = {
  build: Build;
};
const BuildStatusIcon = forwardRef(({ build, ...rest }: BuildStatusIconProps, ref): JSX.Element => {
  const statusName = getBuildStatusText(build);

  return (
    <Tooltip label={statusName}>
      <Icon
        name={getBuildStatusIconName(build)}
        size="24"
        textColor={getBuildStatusColors(build).color}
        zIndex={1}
        {...rest}
        ref={ref}
      />
    </Tooltip>
  );
});

type BuildCommitMessageAndPrIdOrCommitHashProps = {
  build: Build;
  app?: AppForAppPage;
};
const BuildCommitMessageAndPrIdOrCommitHash = ({
  build,
  app,
}: BuildCommitMessageAndPrIdOrCommitHashProps): JSX.Element => {
  const appSlug = build.repository?.slug || app?.slug;
  const gitProvider = build.repository?.provider || app?.provider;
  const gitOwner = build.repository?.repoOwner || app?.gitOwner;
  const gitRepoSlug = build.repository?.repoSlug || app?.gitRepoSlug;
  const commitHashUrl = getGitProviderCommitHashUrl(
    gitProvider,
    gitOwner,
    gitRepoSlug,
    build.commitHash,
    build.pullRequestId,
  );

  const pullRequestUrl = getGitProviderPullRequestUrl(gitProvider, gitOwner, gitRepoSlug, build.pullRequestId);

  let url: string;
  if (build?.isPipeline) {
    url = pipelineDetailsPagePath(appSlug, build.slug);
  } else {
    url = buildDetailsPagePath(build.slug);
  }

  return (
    <Box
      display="flex"
      flexDirection={['column', 'row']}
      gap="4"
      justifyContent="flex-start"
      alignItems="center"
      overflow="hidden"
    >
      <LinkOverlay
        flexShrink={1}
        minWidth="0"
        fontSize="2"
        lineHeight="20px"
        href={url}
        maxWidth="720px"
        textOverflow="ellipsis"
        overflow="hidden"
        whiteSpace="nowrap"
      >
        {build.commitMessage || 'No commit message'}
      </LinkOverlay>
      <PullRequestOrCommitHashLink
        size="2"
        gitProvider={gitProvider}
        commitHash={build.commitHash}
        commitHashUrl={commitHashUrl}
        pullRequestId={build.pullRequestId}
        pullRequestUrl={pullRequestUrl}
      />
      <InstallableArtifacts artifacts={build.artifacts} paddingInlineStart={[0, '4']} />
    </Box>
  );
};

type BuildActionProps = {
  build: Build;
  onRebuildClick: (baseBuild: Build) => void;
  onAbortClick: (baseBuild: Build) => void;
};
const BuildAction = ({ build, onRebuildClick, onAbortClick }: BuildActionProps): JSX.Element => {
  const { isMobile } = useResponsive();
  const [isActionLoading, setActionLoading] = useState(false);

  const isNotRebuildable: boolean = Boolean(build.finishedAt && !build.rebuildable);
  const actionIcon: TypeIconName = build.finishedAt ? 'Replace' : 'Cross';
  const actionText = build.finishedAt ? 'Rebuild' : 'Abort';
  const actionTextColor: TypeColors = build.finishedAt ? 'purple.50' : 'red.50';
  const actionButtonHoverBackgroundColor: TypeColors = build.finishedAt ? 'purple.90' : 'red.90';
  const handleBuildAction: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    if (build.finishedAt) {
      setActionLoading(true);
    }
    return build.finishedAt ? onRebuildClick(build) : onAbortClick(build);
  };

  return (
    <Tooltip shouldWrapChildren={isNotRebuildable} isDisabled={!isNotRebuildable} label="Not rebuildable">
      <Box zIndex={1} position="relative" cursor="not-allowed">
        <Button
          as="button"
          variant="tertiary"
          size={isMobile ? 'lg' : 'sm'}
          leftIconName={actionIcon}
          color={actionTextColor}
          textColor={actionTextColor}
          onClick={handleBuildAction}
          marginRight={12}
          isLoading={isActionLoading}
          zIndex={1}
          isDisabled={isNotRebuildable}
          _hover={
            isActionLoading
              ? undefined
              : {
                  backgroundColor: actionButtonHoverBackgroundColor,
                }
          }
        >
          {actionText}
        </Button>
      </Box>
    </Tooltip>
  );
};

type BuildListItemUpperSectionProps = {
  build: Build;
  app?: AppForAppPage;
  showAppInfo: boolean;
  canRunBuild: boolean;
  onRebuildClick?: (baseBuild: Build) => void;
  onAbortClick?: (baseBuild: Build) => void;
};
const BuildItemUpperSection = ({
  build,
  app,
  showAppInfo,
  canRunBuild,
  onRebuildClick,
  onAbortClick,
}: BuildListItemUpperSectionProps): JSX.Element => {
  const { isMobile } = useResponsive();
  const ref = useRef();
  const shouldShowBuildAction = canRunBuild && onRebuildClick && onAbortClick;

  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        borderBottom="1px solid"
        borderBottomColor="neutral.93"
        _groupHover={{ borderColor: 'neutral.90' }}
      >
        <Box display="flex" padding="12" gap="12" alignItems="center" overflow="hidden" width="100%">
          <BuildStatusIcon build={build} />
          {showAppInfo ? (
            <>
              <AppAvatar app={build?.repository} />
              <Box display="flex" flexDirection="column" flexGrow={1} minWidth="0">
                <AppTitle app={build?.repository} lineHeight="24px" />
                {isMobile ? (
                  <Tooltip label="Build number">
                    <BuildMetaData icon="Numero" gap="0" minWidth="0" ref={ref} zIndex={1}>
                      {build.buildNumber}
                    </BuildMetaData>
                  </Tooltip>
                ) : (
                  <BuildCommitMessageAndPrIdOrCommitHash build={build} app={app} />
                )}
              </Box>
            </>
          ) : (
            <BuildCommitMessageAndPrIdOrCommitHash build={build} app={app} />
          )}
        </Box>
        {shouldShowBuildAction && (
          <BuildAction build={build} onRebuildClick={onRebuildClick} onAbortClick={onAbortClick} />
        )}
      </Box>
      {showAppInfo && isMobile ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          gap="4"
          padding="12"
          borderTop="1px solid"
          borderTopColor="neutral.93"
          flexGrow={1}
        >
          <BuildCommitMessageAndPrIdOrCommitHash build={build} app={app} />
        </Box>
      ) : null}
    </>
  );
};

export default BuildItemUpperSection;
